/**
 * AIQualifications — full transparency page.
 *
 * Shows every AI persona's:
 *   • Identity, tier, role
 *   • Discipline field + historical arc (the "1,000 years of mastery")
 *   • Current age + cycles completed + current focus
 *   • Doctrine (their system prompt — what they always do)
 *   • Recent refined principles (last 5 Shu'aib lessons)
 *
 * Filterable by tier. Read-only. Public-facing inside the dashboard so
 * users understand what each AI on their team is actually expert in.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Scale, ShieldAlert, Cpu, Crown, Workflow, DollarSign, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap,
  Globe, Sparkles, ChevronDown, ChevronUp, Loader2,
  GitCompare, Check, X as XIcon,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ICON_MAP = {
  Scale, ShieldAlert, Cpu, Crown, Workflow, DollarSign, Megaphone,
  Users, BarChart3, BookCheck, HeartHandshake, ShieldCheck, Radar,
  GraduationCap, Library, HeartPulse, MessagesSquare,
  Anchor, Stethoscope, Hammer, Plane, Factory, Sprout, School, Zap, Globe,
};

const TIER_LABEL = {
  leadership: 'Senior Leadership',
  specialist: 'Specialists',
  industry:   'Industry Experts',
  security:   'Security AIs',
  mentor:     'Mentors',
  concierge:  'Product Concierge',
};
const TIER_ORDER = ['leadership', 'industry', 'specialist', 'mentor', 'security', 'concierge'];

export default function AIQualifications() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('all');
  const [expanded, setExpanded] = useState({}); // {persona_id: bool}
  const [compareMode, setCompareMode] = useState(false);
  const [picked, setPicked] = useState([]); // up to 2 persona_ids

  const togglePick = (id) => {
    setPicked((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 2) return [cur[1], id]; // FIFO drop oldest
      return [...cur, id];
    });
  };

  useEffect(() => {
    api.get('/ai/employees/qualifications')
      .then(({ data }) => setList(data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load qualifications.'))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of list) (g[p.tier] ||= []).push(p);
    return g;
  }, [list]);

  const visibleTiers = tierFilter === 'all'
    ? TIER_ORDER.filter((t) => grouped[t]?.length)
    : [tierFilter];

  if (loading) {
    return (
      <div className="ve-section text-sm text-slate-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin"/> Loading the team&rsquo;s qualifications…
      </div>
    );
  }

  return (
    <div className="ve-section" data-testid="ai-qualifications-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <Sparkles size={14} className="text-[#8B3FC5]"/>
            AI Qualifications
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            The full <span className="ve-gold-text">curriculum vitae</span> of your AI team.
          </h1>
          <p className="text-sm text-slate-600 dark:text-white/70 mt-2 max-w-2xl">
            Every advisor&rsquo;s doctrine, historical arc, current focus and recently refined principles. Click any card to expand.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="qual-compare-toggle"
            onClick={() => { setCompareMode((v) => !v); setPicked([]); }}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md border text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap transition
              ${compareMode
                ? 'border-[#0A0A0A] bg-[#0A0A0A] text-[#8B3FC5]'
                : 'border-[#8B3FC5] text-[#5E1F8B] dark:text-[#8B3FC5] hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a]/40'}`}
          >
            <GitCompare size={11}/>
            {compareMode ? `Compare · ${picked.length}/2 picked` : 'Compare AIs'}
          </button>
          <TierPill
            active={tierFilter === 'all'} label={`All · ${list.length}`}
            onClick={() => setTierFilter('all')} testid="qual-tier-all"
          />
          {TIER_ORDER.filter((t) => grouped[t]?.length).map((t) => (
            <TierPill
              key={t}
              active={tierFilter === t}
              label={`${TIER_LABEL[t]} · ${grouped[t].length}`}
              onClick={() => setTierFilter(t)}
              testid={`qual-tier-${t}`}
            />
          ))}
        </div>
      </div>

      {/* Compare panel — appears when 2 AIs are picked */}
      {compareMode && picked.length === 2 && (
        <ComparePanel
          a={list.find((p) => p.id === picked[0])}
          b={list.find((p) => p.id === picked[1])}
          onClose={() => setPicked([])}
        />
      )}

      {visibleTiers.map((tier) => (
        <div key={tier} className="mt-8">
          <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">{TIER_LABEL[tier]}</div>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {grouped[tier].map((p) => (
              <QualificationCard
                key={p.id}
                p={p}
                expanded={!!expanded[p.id]}
                toggle={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                compareMode={compareMode}
                isPicked={picked.includes(p.id)}
                onPick={() => togglePick(p.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function QualificationCard({ p, expanded, toggle, compareMode, isPicked, onPick }) {
  const Icon = ICON_MAP[p.icon] || Sparkles;
  return (
    <div
      className={`ve-card p-5 transition-all ${isPicked ? 'ring-2 ring-[#8B3FC5] shadow-[0_0_24px_-8px_rgba(174,82,234,0.7)]' : ''}`}
      data-testid={`qual-card-${p.id}`}
    >
      {/* Compare-mode checkbox */}
      {compareMode && (
        <button
          onClick={onPick}
          data-testid={`qual-pick-${p.id}`}
          className={`mb-2 inline-flex items-center gap-1.5 px-2 h-6 rounded text-[10px] uppercase tracking-[0.18em] font-bold transition
            ${isPicked
              ? 'bg-[#0A0A0A] text-[#8B3FC5]'
              : 'border border-[#8B3FC5]/40 text-[#5E1F8B] dark:text-[#8B3FC5] hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a]/40'}`}
        >
          {isPicked ? <Check size={10}/> : null}
          {isPicked ? 'Selected' : 'Pick to compare'}
        </button>
      )}
      <button
        type="button"
        onClick={toggle}
        className="w-full text-left flex items-start gap-3"
        data-testid={`qual-toggle-${p.id}`}
      >
        <div className="h-11 w-11 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
          <Icon size={20} className="text-[#8B3FC5]"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="display text-lg font-bold leading-tight">{p.english_name}</div>
              <div className="text-[16px] text-[#5E1F8B] dark:text-[#8B3FC5] font-bold leading-tight" dir="rtl">{p.arabic_name}</div>
            </div>
            {p.age_years != null && (
              <span className="shrink-0 text-[9px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4]">
                Age {Number(p.age_years).toLocaleString()}y
              </span>
            )}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/55 font-bold">
            {p.english_role}
          </div>
          <div className="mt-2 text-xs text-slate-700 dark:text-white/80 leading-snug">
            {p.tagline}
          </div>
        </div>
        <div className="text-slate-400">
          {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </div>
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/10 space-y-3 text-xs">
          {/* Field + arc — the 1000-year history */}
          {p.field && (
            <div>
              <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Field</div>
              <div className="mt-1 text-sm font-bold">{p.field}</div>
              <div className="mt-0.5 text-slate-600 dark:text-white/70 leading-relaxed">{p.arc}</div>
            </div>
          )}

          {/* Current focus */}
          {p.current_focus && (
            <div>
              <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Currently distilling</div>
              <div className="mt-1 text-slate-700 dark:text-white/80 leading-relaxed italic">{p.current_focus}</div>
            </div>
          )}

          {/* Recent principles */}
          {p.recent_principles?.length > 0 && (
            <div>
              <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Recently refined principles</div>
              <ul className="mt-1.5 space-y-1">
                {p.recent_principles.map((lsn, i) => (
                  <li key={i} className="text-slate-700 dark:text-white/80 leading-snug">
                    <span className="text-[#5E1F8B] dark:text-[#8B3FC5] font-bold mr-1">›</span>
                    {lsn.new_principle}
                    {lsn.age_at_lesson && (
                      <span className="ml-2 text-[9px] uppercase tracking-[0.18em] text-slate-400">at age {lsn.age_at_lesson.toLocaleString()}y</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Doctrine — the system prompt */}
          {p.doctrine && (
            <div>
              <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Doctrine</div>
              <p className="mt-1 text-slate-600 dark:text-white/70 leading-relaxed whitespace-pre-line">
                {p.doctrine}
              </p>
            </div>
          )}

          {/* Stats */}
          {(p.cycles_completed != null || p.age_years != null) && (
            <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/55 font-bold pt-1">
              <span>Cycles · {p.cycles_completed || 0}</span>
              {p.age_years != null && <span>Mastery · {Number(p.age_years).toLocaleString()} years</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TierPill({ active, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md border text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap transition
        ${active
          ? 'border-[#0A0A0A] bg-[#0A0A0A] text-[#8B3FC5]'
          : 'border-slate-200 dark:border-white/10 text-slate-700 dark:text-white/75 hover:border-[#8B3FC5]'}`}
    >{label}</button>
  );
}

function ComparePanel({ a, b, onClose }) {
  if (!a || !b) return null;
  const IconA = ICON_MAP[a.icon] || Sparkles;
  const IconB = ICON_MAP[b.icon] || Sparkles;
  return (
    <div
      data-testid="qual-compare-panel"
      className="mt-6 ve-card p-6 border-2 border-[#8B3FC5]/60 bg-gradient-to-br from-[#FFF8E5]/40 to-white/40 dark:from-[#3a2f0a]/30 dark:to-transparent"
    >
      <div className="flex items-start justify-between">
        <div className="ve-eyebrow flex items-center gap-2">
          <GitCompare size={12} className="text-[#8B3FC5]"/> Side by side
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10" title="Clear">
          <XIcon size={14}/>
        </button>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
        {[a, b].map((p, idx) => {
          const Icon = idx === 0 ? IconA : IconB;
          return (
            <div key={p.id} className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                  <Icon size={22} className="text-[#8B3FC5]"/>
                </div>
                <div className="min-w-0">
                  <div className="display text-xl font-bold leading-tight">{p.english_name}</div>
                  <div className="text-[16px] text-[#5E1F8B] dark:text-[#8B3FC5] font-bold leading-tight" dir="rtl">{p.arabic_name}</div>
                </div>
              </div>
              <ComparisonRow label="Role" value={p.english_role}/>
              <ComparisonRow label="Tier" value={(p.tier || '').toUpperCase()}/>
              <ComparisonRow label="Field" value={p.field || '—'}/>
              <ComparisonRow label="Mastery age" value={p.age_years != null ? `${Number(p.age_years).toLocaleString()} y` : '—'}/>
              <ComparisonRow label="Cycles completed" value={p.cycles_completed != null ? p.cycles_completed : '—'}/>
              <ComparisonRow label="Tagline" value={p.tagline} wrap/>
              <ComparisonRow label="Current focus" value={p.current_focus || '—'} wrap italic/>
              <ComparisonRow label="Historical arc" value={p.arc || '—'} wrap/>
              {p.recent_principles?.length > 0 && (
                <div>
                  <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] mb-1">Recent principles</div>
                  <ul className="text-xs space-y-1">
                    {p.recent_principles.slice(0, 3).map((lsn, i) => (
                      <li key={i} className="text-slate-700 dark:text-white/80 leading-snug">
                        <span className="text-[#5E1F8B] dark:text-[#8B3FC5] font-bold mr-1">›</span>
                        {lsn.new_principle}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-6 pt-4 border-t border-slate-200 dark:border-white/10 text-xs text-slate-500 dark:text-white/55">
        Pick a different AI above to swap one side. Use the &ldquo;Compare AIs&rdquo; toggle to exit.
      </div>
    </div>
  );
}

function ComparisonRow({ label, value, wrap, italic }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">{label}</div>
      <div className={`text-sm text-slate-800 dark:text-white/85 ${italic ? 'italic' : ''} ${wrap ? 'leading-relaxed' : 'truncate'}`}>
        {value}
      </div>
    </div>
  );
}

